/** @file Utilities for tracking previously used tenants in localStorage. */
import moment from 'moment';
import { Either, Left } from 'purify-ts';
import * as codec from 'purify-ts/Codec';
import { Maybe } from '@codegrade/maybe';
/** Codec for a simplified version of the `Tenant` type from the API, containing only the `id` and `name` fields. */
export const simpleTenantParser = codec.Codec.interface({
    id: codec.string,
    name: codec.string,
});
/** Codec for a stored tenant entry, which includes a timestamp for TTL purposes. */
const storedTenantParser = codec.Codec.interface({
    id: codec.string,
    name: codec.string,
    addedAt: codec.number,
});
/** The TTL for a previously used tenant record, in milliseconds (30 days). */
const TTL = moment.duration(30, 'days').asMilliseconds();
/** A parser to grab previously used tenants from the localStorage. */
const previouslyUsedTenantsParser = codec.Codec.custom({
    decode: (input) => {
        if (input == null) {
            return Left('Input is null');
        }
        if (typeof input !== 'string') {
            return Left('Input is not a string');
        }
        return Either.encase(() => JSON.parse(input))
            .mapLeft(() => `Failed to parse input as JSON: ${input}`)
            .chain(codec.array(storedTenantParser).decode);
    },
    encode: (list) => JSON.stringify(list),
});
/** The key to store previously used tenants under in the local storage. */
export const PREVIOUSLY_USED_TENANTS_STORAGE_KEY = 'cg-previouslyUsedTenants';
/**
 * Just - There are previously used tenants in record.
 * Nothing - There is no record of previously used tenants.
 *
 * @returns Maybe a list of previously used tenants.
 */
export function getPreviouslyUsedTenants() {
    const now = Date.now();
    return Maybe.fromEither(previouslyUsedTenantsParser.decode(localStorage.getItem(PREVIOUSLY_USED_TENANTS_STORAGE_KEY)))
        .map(list => list.filter(item => now - item.addedAt < TTL))
        .filter(list => list.length > 0);
}
/**
 * Adds a tenant to the previously-used list in localStorage, replacing any existing entry with the same ID.
 *
 * @param item The item to be added into the list.
 */
export function addPreviouslyUsedTenant(item) {
    const now = Date.now();
    const currentStoredList = Maybe.fromEither(previouslyUsedTenantsParser.decode(localStorage.getItem(PREVIOUSLY_USED_TENANTS_STORAGE_KEY)))
        // Filter out out-dated items
        .map(list => list.filter(stored => now - stored.addedAt < TTL))
        // Filter out items with the same ID
        // We want to remove old entry and add a new one with up-to-date timestamp
        .map(list => list.filter(stored => stored.id !== item.id))
        .orDefault([]);
    const newList = [...currentStoredList, { ...item, addedAt: now }].slice(-3); // Save only the last 3 tenants
    localStorage.setItem(PREVIOUSLY_USED_TENANTS_STORAGE_KEY, previouslyUsedTenantsParser.encode(newList));
}
